import api from './api';

/**
 * Client for the Media Library management endpoints.
 *
 * Folders:        /api/media/folders
 * Library items:  /api/media/library, /api/media/library/{id}, .../favorites, .../folder/{folderId}
 *
 * All endpoints are owner-scoped on the backend by the authenticated user.
 */
const mediaLibraryService = {
    // ==================== FOLDERS ====================

    /**
     * @param {'flat' | 'tree'} [view='tree']
     * @returns {Promise<Array>} list of MediaFolderDTO
     */
    listFolders: async (view = 'tree') => {
        const response = await api.get('/media/folders', { params: { view } });
        return response.data;
    },

    getFolder: async (folderId) => {
        const response = await api.get(`/media/folders/${folderId}`);
        return response.data;
    },

    /**
     * @param {{name: string, parentId?: string|null, color?: string, icon?: string}} payload
     */
    createFolder: async (payload) => {
        const response = await api.post('/media/folders', payload);
        return response.data;
    },

    /**
     * @param {string} folderId
     * @param {{name?: string, parentId?: string|null, color?: string, icon?: string}} payload
     */
    updateFolder: async (folderId, payload) => {
        const response = await api.patch(`/media/folders/${folderId}`, payload);
        return response.data;
    },

    deleteFolder: async (folderId) => {
        await api.delete(`/media/folders/${folderId}`);
    },

    assignMediaToFolder: async (folderId, mediaFileId) => {
        await api.post(`/media/folders/${folderId}/media/${mediaFileId}`);
    },

    unassignMediaFromFolder: async (folderId, mediaFileId) => {
        await api.delete(`/media/folders/${folderId}/media/${mediaFileId}`);
    },

    // ==================== LIBRARY ITEMS ====================

    /**
     * @param {{page?: number, size?: number, sort?: string, mediaType?: string|null, search?: string|null}} [pageable]
     */
    listFavorites: async ({ page = 0, size = 24, sort = 'createdAt,desc', mediaType = null, search = null } = {}) => {
        const params = { page, size, sort };
        if (mediaType) params.mediaType = mediaType;
        if (search) params.search = search;
        const response = await api.get('/media/library/favorites', { params });
        return response.data;
    },

    listByFolder: async (folderId, { page = 0, size = 24, sort = 'createdAt,desc', mediaType = null, search = null } = {}) => {
        const params = { page, size, sort };
        if (mediaType) params.mediaType = mediaType;
        if (search) params.search = search;
        const response = await api.get(`/media/library/folder/${folderId}`, { params });
        return response.data;
    },

    /**
     * @param {string} mediaFileId
     * @param {{displayName?: string, isFavorite?: boolean}} payload
     */
    updateItem: async (mediaFileId, payload) => {
        const response = await api.patch(`/media/library/${mediaFileId}`, payload);
        return response.data;
    },

    deleteItem: async (mediaFileId) => {
        await api.delete(`/media/library/${mediaFileId}`);
    },

    restoreItem: async (mediaFileId) => {
        await api.post(`/media/library/${mediaFileId}/restore`);
    },
};

export default mediaLibraryService;
